/**
 * useMediaServerEventStream — Singleton SSE subscription hook for media server events.
 *
 * Opens exactly ONE EventSource to /api/streaming/events/sse regardless of how
 * many components call this hook. The underlying connection is ref-counted:
 * started when the first subscriber attaches, closed when the last one leaves.
 *
 * Usage:
 *   useMediaServerEventStream("session.start", (event) => { ... });
 *   useMediaServerEventStream("library.changed", (event) => { ... });
 *
 * Connection status:
 *   const status = useMediaServerEventStreamStatus(); // 'connecting' | 'open' | 'closed'
 */
import { useCallback, useEffect, useRef, useState } from "react";

import type {
  MediaServerEvent,
  MediaServerEventKind,
} from "../types/streamingEvents.js";

// ─────────────────────────────────────────────────────────────────────────────
// Module-scoped singleton controller
// ─────────────────────────────────────────────────────────────────────────────

// Internal: typed listener stored as (kind, handler) pair
interface StoredListener {
  kind: MediaServerEventKind;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handler: (event: any) => void;
}

export type MediaServerStreamStatus = "connecting" | "open" | "closed";

// Status watchers: components that want to re-render on status changes
type StatusWatcher = (status: MediaServerStreamStatus) => void;

const SSE_URL = "/api/streaming/events/sse";
const MAX_RECONNECT_DELAY_MS = 30_000;

let _eventSource: EventSource | null = null;
let _subscriberCount = 0;
let _status: MediaServerStreamStatus = "closed";
let _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let _retryCount = 0;

const _listeners: Set<StoredListener> = new Set();
const _statusWatchers: Set<StatusWatcher> = new Set();

function _notifyStatus(s: MediaServerStreamStatus) {
  _status = s;
  for (const w of _statusWatchers) {
    w(s);
  }
}

function _dispatchToListeners(kind: MediaServerEventKind, raw: string) {
  let parsed: MediaServerEvent;
  try {
    parsed = JSON.parse(raw) as MediaServerEvent;
  } catch {
    return;
  }
  for (const entry of _listeners) {
    if (entry.kind === kind) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        entry.handler(parsed);
      } catch {
        // Misbehaving listener — remove it to prevent cascading failures
        _listeners.delete(entry);
      }
    }
  }
}

function _clearReconnectTimer() {
  if (_reconnectTimer !== null) {
    clearTimeout(_reconnectTimer);
    _reconnectTimer = null;
  }
}

// All known event kinds the SSE relay emits
const ALL_KINDS: MediaServerEventKind[] = [
  "session.start",
  "session.update",
  "session.end",
  "library.changed",
  "activity.update",
  "transcode.update",
];

function _startEventSource() {
  if (typeof EventSource === "undefined") return;

  _clearReconnectTimer();

  if (_eventSource) {
    _eventSource.close();
    _eventSource = null;
  }

  _notifyStatus("connecting");

  const es = new EventSource(SSE_URL);
  _eventSource = es;

  es.addEventListener("open", () => {
    _retryCount = 0;
    _notifyStatus("open");
  });

  // Register named event listeners for all known kinds
  for (const kind of ALL_KINDS) {
    es.addEventListener(kind, (e: Event) => {
      _dispatchToListeners(kind, (e as MessageEvent).data as string);
    });
  }

  es.addEventListener("error", () => {
    // Only reconnect if we still have subscribers
    if (_subscriberCount === 0) return;

    _eventSource?.close();
    _eventSource = null;
    _notifyStatus("connecting");

    const delay = Math.min(MAX_RECONNECT_DELAY_MS, 1000 * 2 ** _retryCount);
    _retryCount += 1;
    _reconnectTimer = setTimeout(() => {
      _reconnectTimer = null;
      if (_subscriberCount > 0) {
        _startEventSource();
      }
    }, delay);
  });
}

function _stopEventSource() {
  _clearReconnectTimer();
  if (_eventSource) {
    _eventSource.close();
    _eventSource = null;
  }
  _notifyStatus("closed");
}

function _addSubscriber() {
  _subscriberCount += 1;
  if (_subscriberCount === 1) {
    _startEventSource();
  }
}

function _removeSubscriber() {
  _subscriberCount = Math.max(0, _subscriberCount - 1);
  if (_subscriberCount === 0) {
    _stopEventSource();
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Public hooks
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Subscribe to a specific media server event kind.
 *
 * The handler receives fully-typed events. The singleton EventSource is opened
 * when the first component subscribes and closed when the last one unmounts.
 */
export function useMediaServerEventStream<K extends MediaServerEventKind>(
  kind: K,
  handler: (event: Extract<MediaServerEvent, { kind: K }>) => void
): void {
  // Stable ref so handler identity changes don't retrigger the effect
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const entry: StoredListener = {
      kind,
      // Wrap through ref so the caller can update the callback without re-subscribing
      handler: (event: unknown) => {
        handlerRef.current(event as Extract<MediaServerEvent, { kind: K }>);
      },
    };

    _listeners.add(entry);
    _addSubscriber();

    return () => {
      _listeners.delete(entry);
      _removeSubscriber();
    };
    // `kind` changes are handled by cleanup+re-subscribe
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);
}

/**
 * Returns the current SSE connection status for UI indicators.
 *
 * Re-renders the component whenever the underlying connection state changes.
 * Multiple components can call this hook — they all share the same module-scoped status.
 */
export function useMediaServerEventStreamStatus(): MediaServerStreamStatus {
  const [status, setStatus] = useState<MediaServerStreamStatus>(_status);

  // Stable callback ref — avoids stale closure while being referentially stable
  const setStatusRef = useRef(setStatus);
  setStatusRef.current = setStatus;

  const watcher = useCallback<StatusWatcher>((s) => {
    setStatusRef.current(s);
  }, []);

  useEffect(() => {
    // Sync immediately in case status changed between render and effect
    setStatus(_status);
    _statusWatchers.add(watcher);

    return () => {
      _statusWatchers.delete(watcher);
    };
  }, [watcher]);

  return status;
}
